/**
 * Client-side image processing.
 *
 * Workers have no image pipeline, so the phone does the resizing: a full-size
 * capture becomes a ~1600px display image plus a 400px thumbnail before it
 * ever touches the network. On a job-site connection that is the difference
 * between an update landing and an update timing out.
 */

export interface ProcessedImage {
  full: Blob
  thumb: Blob
  width: number
  height: number
}

export interface ProcessOptions {
  maxDimension?: number
  quality?: number
  thumbDimension?: number
}

async function loadBitmap(file: Blob): Promise<ImageBitmap> {
  // `imageOrientation: 'from-image'` applies the EXIF rotation phones record,
  // otherwise portrait photos land on their side.
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    return await createImageBitmap(file)
  }
}

function fit(width: number, height: number, max: number): { w: number; h: number } {
  if (width <= max && height <= max) return { w: width, h: height }
  const scale = max / Math.max(width, height)
  return { w: Math.round(width * scale), h: Math.round(height * scale) }
}

async function render(bitmap: ImageBitmap, w: number, h: number, quality: number): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(w, h)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not prepare the photo on this device')
    ctx.drawImage(bitmap, 0, 0, w, h)
    return canvas.convertToBlob({ type: 'image/jpeg', quality })
  }

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not prepare the photo on this device')
  ctx.drawImage(bitmap, 0, 0, w, h)
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not prepare the photo'))),
      'image/jpeg',
      quality,
    )
  })
}

export async function processImage(
  file: File, options: ProcessOptions = {},
): Promise<ProcessedImage> {
  const maxDimension = options.maxDimension ?? 1600
  const quality = options.quality ?? 0.75
  const thumbDimension = options.thumbDimension ?? 400

  const bitmap = await loadBitmap(file)
  try {
    const full = fit(bitmap.width, bitmap.height, maxDimension)
    const thumb = fit(bitmap.width, bitmap.height, thumbDimension)
    return {
      full: await render(bitmap, full.w, full.h, quality),
      thumb: await render(bitmap, thumb.w, thumb.h, 0.6),
      width: full.w,
      height: full.h,
    }
  } finally {
    bitmap.close()
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
